import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Check,
    Loader2,
    Plus,
    Scale,
    Trash2,
    X,
} from 'lucide-react';
import { Customer } from '../../types';
import { Button } from '../Button';
import { CustomerPickerDialog } from '../crm/CustomerPickerDialog';
import { BUCHHALTUNG_NUR_LESEN_HINWEIS } from './rechnungRechte';
import { fetchCustomers } from '../../services/crmService';
import { describeError } from '../../services/errorMessage';
import {
    ProjectSplit,
    SHARE_FULL_BP,
    formatShare,
    validateSplit,
} from '../../services/projectSplit';
import {
    deleteProjectSplit,
    fetchProjectSplit,
    saveProjectSplit,
} from '../../services/projectSplitService';
import {
    SplitDraftParticipant,
    distributeEvenly,
    draftFromSplit,
    formatSharePercent,
    parseSharePercent,
    parseSplitDraft,
    rowSumBp,
} from './splitKeyLogic';

/**
 * Bedienoberflaeche zum Aufteilungsschluessel: EIN Projekt, mehrere
 * Auftraggeber, je eine eigene Rechnung.
 *
 * Die Darstellung ist eine MATRIX und keine Liste je Auftraggeber. Der Fehler,
 * den dieses Modul verhindern soll, ist immer derselbe: die Anteile EINER
 * Position ergeben nicht 100 %. Dann fordert entweder niemand den Rest (das
 * Geld faellt lautlos aus), oder zwei Auftraggeber tragen dieselbe Leistung
 * (Doppelforderung). Beides faellt an keinem einzelnen Beleg auf — jeder fuer
 * sich sieht richtig aus, erst ihre SUMME ist falsch. Genau diese Summe ist in
 * der Matrix eine Zeile, und sie steht sichtbar am Zeilenende.
 *
 * Gerechnet wird in ganzen BASISPUNKTEN (100,00 % = 10000), getippt wird in
 * Prozent mit deutschem Komma. Die Umrechnung und jede andere nicht-triviale
 * Rechnung liegen in splitKeyLogic.ts — das Projekt hat keine
 * jsdom-Testumgebung, und was in dieser Datei steht, ist damit nicht testbar.
 * Hier bleibt deshalb nur, was ohne Bildschirm ohnehin nichts bedeutet:
 * Zustand, Ereignisse und Darstellung.
 */

export interface SplitKeyEditorProps {
    projectId: string;
    /**
     * Die LV-Positionen des Projekts. Sie kommen von aussen, weil nur der
     * Aufrufer weiss, welche Positionen ueberhaupt abgerechnet werden — und
     * weil die Pruefsumme genau gegen DIESE Liste laufen muss: eine Position
     * ohne Ausnahme fiele sonst aus der Pruefung, obwohl gerade sie die
     * Grundquote traegt.
     */
    services: { id: string; lvPosition?: string; title: string }[];
    onClose: () => void;
    /** Name oder Nummer des Projekts — nur fuer die Kopfzeile. */
    projectLabel?: string;
    /** Nach erfolgreichem Speichern, damit der Aufrufer nachziehen kann. */
    onSaved?: (split: ProjectSplit) => void;
    /**
     * Bereits abgerechnete Menge OHNE Auftraggeber — Altbestand aus der Zeit
     * vor der Aufteilung.
     *
     * Sie ist kein Schoenheitsfehler, sondern eine Sperre: solange sie steht,
     * laesst sich der Anteil eines Beteiligten nicht ausrechnen, und keine
     * Teilrechnung entsteht. Deshalb gehoert sie in diesen Dialog — hier sitzt
     * der, der sie aufloesen kann.
     */
    unassignedBilled?: number;
    /**
     * Den Altbestand einem Beteiligten zuschlagen. Ohne den Rueckruf wird er
     * nur gemeldet.
     */
    onAssignLegacy?: (party: { partyId: string; customerId: string }) => Promise<void>;
    /**
     * Lesemodus der Buchhaltung (ohne Recht „Rechnungen bearbeiten“): der
     * Schlüssel steht da wie gespeichert, jede Eingabe und jeder Knopf außer
     * Schließen ist gesperrt.
     */
    nurLesen?: boolean;
}

export const SplitKeyEditor: React.FC<SplitKeyEditorProps> = ({
    projectId,
    services,
    onClose,
    projectLabel,
    onSaved,
    unassignedBilled,
    onAssignLegacy,
    nurLesen = false,
}) => {
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [draft, setDraft] = useState<SplitDraftParticipant[]>([]);
    const [customers, setCustomers] = useState<Customer[] | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savedNote, setSavedNote] = useState<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    /** Lag beim Oeffnen bereits ein Schluessel vor? Nur dann gibt es etwas aufzuheben. */
    const [hadStored, setHadStored] = useState(false);

    // Schluessel und Kundenliste in EINEM Anlauf. Die Namen der Spalten stehen
    // in den Kunden, nicht im Schluessel — nacheinander geladen zeigte die
    // Matrix fuer einen Moment nackte Kennungen.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([fetchProjectSplit(projectId), fetchCustomers()])
            .then(([split, list]) => {
                if (cancelled) return;
                setCustomers(list);
                setDraft(draftFromSplit(split));
                setHadStored((split?.participants?.length ?? 0) > 0);
            })
            .catch((e) => {
                if (!cancelled) setLoadError(describeError(e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    const customerById = useMemo(() => {
        const map = new Map<string, Customer>();
        for (const c of customers ?? []) map.set(c.id, c);
        return map;
    }, [customers]);

    const serviceIds = useMemo(() => services.map((s) => s.id), [services]);

    const { split, unreadable } = useMemo(
        () => parseSplitDraft(projectId, draft),
        [projectId, draft],
    );

    /**
     * Die Pruefung des Rechenkerns, gegen die uebergebenen Positionen.
     *
     * Solange eine Zelle unlesbar ist, wird gar nicht erst geprueft: die
     * unlesbare Zelle geht als 0 in den Schluessel ein und erzeugte damit eine
     * Fehlermeldung ueber eine Pruefsumme, die es so nicht gibt.
     */
    const issues = useMemo(
        () => (unreadable.length > 0 ? [] : validateSplit(serviceIds, split)),
        [unreadable.length, serviceIds, split],
    );

    const sumMismatchIds = useMemo(
        () => new Set(issues.filter((i) => i.code === 'sum_mismatch').map((i) => i.serviceId)),
        [issues],
    );

    const unreadableKeys = useMemo(
        () => new Set(unreadable.map((c) => `${c.partyId}::${c.serviceId ?? ''}`)),
        [unreadable],
    );

    const baseSum = useMemo(() => rowSumBp(split.participants), [split.participants]);

    const canSave =
        !nurLesen && !loading && !saving && unreadable.length === 0 && issues.length === 0 && draft.length > 0;
    /** Eingaben und Knöpfe gesperrt: beim Speichern und im Lesemodus. */
    const gesperrt = saving || nurLesen;
    /** Titel der gesperrten Knöpfe im Lesemodus; fehlt sonst. */
    const sperrHinweis = nurLesen ? BUCHHALTUNG_NUR_LESEN_HINWEIS : undefined;

    // ─── Bearbeiten ─────────────────────────────────────────────────────────

    const setDefaultInput = useCallback((partyId: string, value: string) => {
        setSavedNote(null);
        setDraft((rows) =>
            rows.map((r) => (r.partyId === partyId ? { ...r, defaultInput: value } : r)),
        );
    }, []);

    /** Den Abschnitt einer Spalte benennen ("Haus 3"). */
    const setLabel = useCallback((partyId: string, value: string) => {
        setSavedNote(null);
        setDraft((rows) => rows.map((r) => (r.partyId === partyId ? { ...r, label: value } : r)));
    }, []);

    /**
     * Eine Ausnahme setzen oder ENTFERNEN.
     *
     * Ein leeres Feld loescht den Eintrag, statt ihn als leeren Text stehen zu
     * lassen. Nur so faellt die Position wieder auf die Grundquote zurueck —
     * ein leerer Eintrag, der bleibt, sieht im Formular wie "erbt" aus, waere
     * aber beim Speichern eine Ausnahme ueber 0 %, und der Auftraggeber
     * verschwaende lautlos aus der Position.
     */
    const setOverrideInput = useCallback(
        (partyId: string, serviceId: string, value: string) => {
            setSavedNote(null);
            setDraft((rows) =>
                rows.map((r) => {
                    if (r.partyId !== partyId) return r;
                    const next = { ...r.overrideInputs };
                    if (value.trim() === '') delete next[serviceId];
                    else next[serviceId] = value;
                    return { ...r, overrideInputs: next };
                }),
            );
        },
        [],
    );

    /**
     * Beim Verlassen des Feldes auf zwei Nachkommastellen bringen.
     *
     * Nur, wenn der Text lesbar IST — ein unlesbarer Eintrag bleibt stehen, wie
     * er getippt wurde. Ihn beim Verlassen zu ueberschreiben naehme dem Nutzer
     * genau die Zeichen weg, an denen er den Tippfehler erkennt.
     */
    const normalizeOnBlur = useCallback(
        (partyId: string, serviceId?: string) => {
            setDraft((rows) =>
                rows.map((r) => {
                    if (r.partyId !== partyId) return r;
                    if (serviceId === undefined) {
                        const bp = parseSharePercent(r.defaultInput);
                        if (bp == null) return r;
                        return { ...r, defaultInput: formatSharePercent(bp) };
                    }
                    const text = r.overrideInputs[serviceId];
                    if (text === undefined) return r;
                    const bp = parseSharePercent(text);
                    if (bp == null) return r;
                    return {
                        ...r,
                        overrideInputs: { ...r.overrideInputs, [serviceId]: formatSharePercent(bp) },
                    };
                }),
            );
        },
        [],
    );

    const addParticipant = useCallback((customer: Customer) => {
        setSavedNote(null);
        setPickerOpen(false);
        setDraft((rows) => {
            // Derselbe Auftraggeber DARF ein zweites Mal — dann aber als eigener
            // ABSCHNITT. Genau das ist der Haus-Fall: ein Bauherr, mehrere
            // Haeuser, je Haus eine Rechnung. Verboten bleibt nur die echte
            // Dublette (gleicher Auftraggeber, gleicher Abschnittsname); sie
            // bekaeme zwei Teilrechnungen ueber denselben Anteil, und die
            // Datenbank lehnte den Eintrag ohnehin ab.
            //
            // Damit die zweite Zeile nicht sofort als Dublette gilt, bekommt sie
            // einen Vorschlagsnamen. Der Nutzer benennt ihn um; leer lassen kann
            // er ihn nur bei der ersten.
            const schonDa = rows.filter((r) => r.customerId === customer.id);
            const label = schonDa.length ? `Abschnitt ${schonDa.length + 1}` : '';
            return [
                ...rows,
                {
                    // Die Kennung entsteht HIER und nicht erst beim Speichern:
                    // die Matrix schluesselt ihre Zellen darueber, und zwei
                    // Spalten desselben Auftraggebers waeren sonst nicht
                    // auseinanderzuhalten.
                    partyId: crypto.randomUUID(),
                    customerId: customer.id,
                    label,
                    defaultInput: '0,00',
                    overrideInputs: {},
                },
            ];
        });
    }, []);

    const removeParticipant = useCallback((partyId: string) => {
        setSavedNote(null);
        setDraft((rows) => rows.filter((r) => r.partyId !== partyId));
    }, []);

    /** Gleichmaessig verteilen — die Grundquoten, ohne die Ausnahmen anzutasten. */
    const spreadEvenly = useCallback(() => {
        setSavedNote(null);
        setDraft((rows) => {
            const shares = distributeEvenly(rows.length);
            return rows.map((r, i) => ({ ...r, defaultInput: formatSharePercent(shares[i]) }));
        });
    }, []);

    /**
     * Positionsweise: alle Grundquoten auf 0.
     *
     * Danach traegt keine Position mehr von sich aus etwas, und der Nutzer
     * ordnet jede einzeln zu. Die Pruefsumme zeigt ihm dabei jede Zeile rot an,
     * die er noch nicht vergeben hat — genau das ist der Zweck.
     */
    const clearBaseShares = useCallback(() => {
        setSavedNote(null);
        setDraft((rows) => rows.map((r) => ({ ...r, defaultInput: '0,00' })));
    }, []);

    // ─── Speichern ──────────────────────────────────────────────────────────

    const save = useCallback(async () => {
        if (!canSave) return;
        setSaving(true);
        setSaveError(null);
        setSavedNote(null);
        try {
            const stored = await saveProjectSplit(split);
            setDraft(draftFromSplit(stored));
            setHadStored(stored.participants.length > 0);
            setSavedNote('Aufteilungsschlüssel gespeichert.');
            onSaved?.(stored);
        } catch (e) {
            setSaveError(describeError(e));
        } finally {
            setSaving(false);
        }
    }, [canSave, split, onSaved]);

    const dropSplit = useCallback(async () => {
        if (saving || nurLesen) return;
        setSaving(true);
        setSaveError(null);
        setSavedNote(null);
        try {
            await deleteProjectSplit(projectId);
            setDraft([]);
            setHadStored(false);
            setSavedNote('Aufteilung aufgehoben — das Projekt rechnet wieder auf einen Auftraggeber ab.');
            onSaved?.({ projectId, participants: [] });
        } catch (e) {
            setSaveError(describeError(e));
        } finally {
            setSaving(false);
        }
    }, [projectId, saving, nurLesen, onSaved]);

    // ─── Darstellung ────────────────────────────────────────────────────────

    const nameOf = (customerId: string): string =>
        customerById.get(customerId)?.name || customerId;

    const subtitleOf = (customerId: string): string => {
        const c = customerById.get(customerId);
        if (!c) return 'Unbekannter Kunde';
        return [c.customerNumber, c.city].filter(Boolean).join(' · ') || '—';
    };

    /** Zelleneingabe: einheitliche Optik, rot bei unlesbarem Text. */
    const cellClass = (bad: boolean): string =>
        `w-24 rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
            bad ? 'border-red-400 bg-red-50' : 'border-slate-300'
        }`;

    const sumCell = (sum: number, blocking: boolean) => (
        <span
            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-bold tabular-nums ${
                sum === SHARE_FULL_BP
                    ? 'bg-emerald-50 text-emerald-700'
                    : blocking
                      ? 'bg-red-50 text-red-700'
                      : 'bg-slate-100 text-slate-500'
            }`}
        >
            {sum === SHARE_FULL_BP ? <Check className="h-3 w-3" /> : null}
            {formatShare(sum)}
        </span>
    );

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
            <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Scale className="h-4 w-4 text-blue-600" />
                        Aufteilungsschlüssel{projectLabel ? ` — ${projectLabel}` : ''}
                        {nurLesen && (
                            <span
                                title={sperrHinweis}
                                className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-700"
                            >
                                Nur lesen
                            </span>
                        )}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-50"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {(unassignedBilled ?? 0) > 0 && (
                    /* Der Altbestand sperrt jede Teilrechnung. Er steht deshalb
                       UEBER der Matrix: wer den Schluessel einrichtet, muss ihn
                       sehen, bevor er die erste Rechnung versucht — sonst
                       begegnet ihm die Sperre erst dort und ohne Zusammenhang. */
                    <div className="mx-5 mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        <p className="font-semibold">
                            {unassignedBilled} Einheiten sind bereits abgerechnet, aber keinem Auftraggeber zugeordnet.
                        </p>
                        <p className="mt-1">
                            Sie stammen aus der Zeit vor der Aufteilung. Solange sie ohne Zuordnung
                            stehen, lässt sich kein Anteil ausrechnen und es entsteht keine
                            Teilrechnung — geraten wäre hier eine Geldforderung an den Falschen.
                        </p>
                        {onAssignLegacy && draft.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <span>Bisher berechnet an:</span>
                                {draft.map((teilnehmer) => (
                                    <button
                                        key={teilnehmer.partyId}
                                        type="button"
                                        disabled={assigning}
                                        onClick={() => {
                                            setAssignError(null);
                                            setAssigning(true);
                                            void onAssignLegacy({
                                                partyId: teilnehmer.partyId,
                                                customerId: teilnehmer.customerId,
                                            })
                                                .catch((e) =>
                                                    setAssignError(e instanceof Error ? e.message : String(e)),
                                                )
                                                .finally(() => setAssigning(false));
                                        }}
                                        className="rounded-lg border border-amber-300 bg-white px-2 py-1 font-semibold hover:bg-amber-100 disabled:opacity-50"
                                    >
                                        {[
                                            (teilnehmer.label || '').trim(),
                                            customers?.find((c) => c.id === teilnehmer.customerId)
                                                ?.name ?? teilnehmer.customerId,
                                        ]
                                            .filter(Boolean)
                                            .join(' — ')}
                                    </button>
                                ))}
                            </div>
                        )}
                        {assignError && <p className="mt-2 font-semibold text-red-600">{assignError}</p>}
                    </div>
                )}
                <div className="space-y-3 overflow-auto p-5">
                    <p className="text-sm text-slate-600">
                        Teilen sich mehrere Auftraggeber dieses Projekt, bekommt jeder von ihnen
                        eine <strong>eigene Rechnung</strong> mit eigener Nummer. Die oberste Zeile
                        ist die Grundquote; eine Zelle darunter bleibt <strong>leer</strong>, solange
                        die Position diese Grundquote erbt. Ein eingetragener Wert ist eine Ausnahme
                        nur für diese Position — Feld leeren nimmt die Ausnahme wieder zurück.
                    </p>

                    {loadError && (
                        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{loadError}</span>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
                            <Loader2 className="h-4 w-4 animate-spin" /> Aufteilungsschlüssel wird
                            geladen …
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    icon={<Plus className="h-3.5 w-3.5" />}
                                    onClick={() => setPickerOpen(true)}
                                    disabled={gesperrt}
                                    title={sperrHinweis}
                                >
                                    Auftraggeber hinzufügen
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={spreadEvenly}
                                    disabled={gesperrt || draft.length === 0}
                                    title={sperrHinweis ?? 'Setzt die Grundquoten zu gleichen Teilen; eingetragene Ausnahmen bleiben stehen.'}
                                >
                                    Gleichmäßig verteilen
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={clearBaseShares}
                                    disabled={gesperrt || draft.length === 0}
                                    title={sperrHinweis ?? 'Setzt alle Grundquoten auf 0,00 % — danach wird jede Position einzeln zugeordnet.'}
                                >
                                    Positionsweise
                                </Button>
                            </div>

                            {draft.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                    Noch kein Auftraggeber im Schlüssel. Dieses Projekt rechnet auf
                                    einen einzigen Empfänger ab.
                                </div>
                            ) : (
                                /* Waagerecht scrollbar: bei fünf Auftraggebern passt die Matrix
                                   nicht mehr in den Dialog. Die Positionsspalte bleibt links
                                   stehen (sticky), die Prüfsumme rechts — ohne beides scrollte
                                   dem Nutzer genau die Information aus dem Bild, an der er
                                   ablesen muss, WELCHE Zeile nicht aufgeht. */
                                <div className="overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="w-full border-collapse text-sm">
                                        <thead>
                                            <tr className="bg-slate-50">
                                                <th className="sticky left-0 z-20 min-w-[16rem] max-w-[22rem] bg-slate-50 px-3 py-2 text-left text-[11px] font-black uppercase tracking-wider text-slate-500">
                                                    LV-Position
                                                </th>
                                                {draft.map((row) => (
                                                    <th
                                                        key={row.partyId}
                                                        className="min-w-[9rem] border-l border-slate-200 px-3 py-2 text-right align-top"
                                                    >
                                                        <div className="flex items-start justify-end gap-1.5">
                                                            <div className="min-w-0 text-right">
                                                                <div className="truncate text-xs font-bold text-slate-700">
                                                                    {nameOf(row.customerId)}
                                                                </div>
                                                                <div className="truncate text-[10px] font-normal text-slate-400">
                                                                    {subtitleOf(row.customerId)}
                                                                </div>
                                                                {/* Der Abschnitt. Leer lassen heisst
                                                                    "kein Abschnitt" — der Bestandsfall,
                                                                    ein Auftraggeber je Spalte. Steht
                                                                    derselbe Auftraggeber mehrfach im
                                                                    Schluessel, ist dieser Name das
                                                                    Einzige, was seine Rechnungen
                                                                    unterscheidet; er wandert deshalb in
                                                                    den Betreff des Belegs. */}
                                                                <input
                                                                    value={row.label}
                                                                    onChange={(e) =>
                                                                        setLabel(
                                                                            row.partyId,
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                    disabled={gesperrt}
                                                                    placeholder="Abschnitt (optional)"
                                                                    title="Bauabschnitt, z. B. „Haus 3“ — steht später im Betreff der Rechnung"
                                                                    className="mt-1 w-full rounded border border-slate-300 px-1.5 py-0.5 text-right text-[11px] font-normal text-slate-600 outline-none placeholder:text-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    removeParticipant(row.partyId)
                                                                }
                                                                disabled={gesperrt}
                                                                title="Auftraggeber aus dem Schlüssel nehmen"
                                                                className="mt-0.5 rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </th>
                                                ))}
                                                <th className="sticky right-0 z-20 border-l border-slate-200 bg-slate-50 px-3 py-2 text-right text-[11px] font-black uppercase tracking-wider text-slate-500">
                                                    Summe
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-slate-100">
                                            {/* Grundquote. Ihre Summe ist bewusst KEIN Blocker:
                                                wer positionsweise aufteilt, steht hier
                                                absichtlich auf 0,00 %. Was zaehlt, sind die
                                                Positionszeilen darunter — nur sie landen auf
                                                einem Beleg. */}
                                            <tr className="bg-blue-50/40">
                                                <td className="sticky left-0 z-10 bg-blue-50/40 px-3 py-2 font-bold text-slate-700">
                                                    Grundquote
                                                    <div className="text-[10px] font-normal text-slate-400">
                                                        gilt für jede Position ohne Ausnahme
                                                    </div>
                                                </td>
                                                {draft.map((row) => (
                                                    <td
                                                        key={row.partyId}
                                                        className="border-l border-slate-200 px-3 py-2 text-right"
                                                    >
                                                        <div className="flex items-center justify-end gap-1">
                                                            <input
                                                                inputMode="decimal"
                                                                value={row.defaultInput}
                                                                onChange={(e) =>
                                                                    setDefaultInput(
                                                                        row.partyId,
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                onBlur={() =>
                                                                    normalizeOnBlur(row.partyId)
                                                                }
                                                                disabled={gesperrt}
                                                                className={cellClass(
                                                                    unreadableKeys.has(
                                                                        `${row.partyId}::`,
                                                                    ),
                                                                )}
                                                            />
                                                            <span className="text-xs text-slate-400">
                                                                %
                                                            </span>
                                                        </div>
                                                    </td>
                                                ))}
                                                <td className="sticky right-0 z-10 border-l border-slate-200 bg-blue-50/40 px-3 py-2 text-right">
                                                    {sumCell(baseSum, false)}
                                                </td>
                                            </tr>

                                            {services.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={draft.length + 2}
                                                        className="px-3 py-6 text-center text-xs text-slate-400"
                                                    >
                                                        Dieses Projekt hat keine LV-Positionen —
                                                        ohne sie lässt sich die Prüfsumme nicht
                                                        rechnen.
                                                    </td>
                                                </tr>
                                            ) : (
                                                services.map((service) => {
                                                    const sum = rowSumBp(
                                                        split.participants,
                                                        service.id,
                                                    );
                                                    const bad = sumMismatchIds.has(service.id);
                                                    return (
                                                        <tr
                                                            key={service.id}
                                                            className={bad ? 'bg-red-50/40' : undefined}
                                                        >
                                                            <td
                                                                className={`sticky left-0 z-10 px-3 py-1.5 ${
                                                                    bad ? 'bg-red-50' : 'bg-white'
                                                                }`}
                                                            >
                                                                <div className="truncate font-medium text-slate-700">
                                                                    {service.lvPosition && (
                                                                        <span className="mr-1.5 text-slate-400 tabular-nums">
                                                                            {service.lvPosition}
                                                                        </span>
                                                                    )}
                                                                    {service.title}
                                                                </div>
                                                            </td>
                                                            {draft.map((row) => {
                                                                const text =
                                                                    row.overrideInputs[service.id] ??
                                                                    '';
                                                                const inherited = text.trim() === '';
                                                                return (
                                                                    <td
                                                                        key={row.partyId}
                                                                        className="border-l border-slate-200 px-3 py-1.5 text-right"
                                                                    >
                                                                        <div className="flex items-center justify-end gap-1">
                                                                            <input
                                                                                inputMode="decimal"
                                                                                value={text}
                                                                                onChange={(e) =>
                                                                                    setOverrideInput(
                                                                                        row.partyId,
                                                                                        service.id,
                                                                                        e.target.value,
                                                                                    )
                                                                                }
                                                                                onBlur={() =>
                                                                                    normalizeOnBlur(
                                                                                        row.partyId,
                                                                                        service.id,
                                                                                    )
                                                                                }
                                                                                disabled={gesperrt}
                                                                                /* Der geerbte Wert steht als
                                                                                   Platzhalter im leeren Feld: so
                                                                                   ist ohne Rechnen sichtbar, was
                                                                                   gilt, und trotzdem
                                                                                   unterscheidbar, ob es geerbt
                                                                                   oder eingetragen ist. */
                                                                                placeholder={formatSharePercent(
                                                                                    parseSharePercent(
                                                                                        row.defaultInput,
                                                                                    ) ?? 0,
                                                                                )}
                                                                                title={
                                                                                    inherited
                                                                                        ? 'Erbt die Grundquote. Ein Wert hier gilt nur für diese Position.'
                                                                                        : 'Ausnahme für diese Position. Feld leeren, dann gilt wieder die Grundquote.'
                                                                                }
                                                                                className={`${cellClass(
                                                                                    unreadableKeys.has(
                                                                                        `${row.partyId}::${service.id}`,
                                                                                    ),
                                                                                )} ${
                                                                                    inherited
                                                                                        ? 'placeholder:text-slate-300'
                                                                                        : 'font-bold text-slate-800'
                                                                                }`}
                                                                            />
                                                                            <span className="text-xs text-slate-400">
                                                                                %
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                );
                                                            })}
                                                            <td
                                                                className={`sticky right-0 z-10 border-l border-slate-200 px-3 py-1.5 text-right ${
                                                                    bad ? 'bg-red-50' : 'bg-white'
                                                                }`}
                                                            >
                                                                {sumCell(sum, true)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {unreadable.length > 0 && (
                                <p className="text-xs text-red-600">
                                    {unreadable.length} Eingabe(n) sind nicht lesbar. Erlaubt sind
                                    0 bis 100 mit höchstens zwei Nachkommastellen, Beispiel: 33,33
                                </p>
                            )}

                            {issues.length > 0 && (
                                <ul className="space-y-0.5 text-xs text-red-600">
                                    {issues.slice(0, 6).map((issue, i) => {
                                        const service = services.find((s) => s.id === issue.serviceId);
                                        const label = service
                                            ? [service.lvPosition, service.title]
                                                  .filter(Boolean)
                                                  .join(' ')
                                            : null;
                                        return (
                                            <li
                                                key={`${issue.code}-${issue.serviceId ?? issue.customerId ?? i}`}
                                                className="flex items-start gap-1.5"
                                            >
                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                <span>
                                                    {label && (
                                                        <strong className="mr-1">{label}:</strong>
                                                    )}
                                                    {issue.message}
                                                </span>
                                            </li>
                                        );
                                    })}
                                    {issues.length > 6 && (
                                        <li className="pl-5 text-slate-500">
                                            … und {issues.length - 6} weitere Zeile(n).
                                        </li>
                                    )}
                                </ul>
                            )}

                            {/* Ein einzelner Teilnehmer mit 100 % ist ausdruecklich KEINE
                                Teilung (hasSplit). Gespeichert werden darf er trotzdem — er
                                ist der Zwischenstand auf dem Weg zum zweiten. */}
                            {draft.length === 1 && unreadable.length === 0 && issues.length === 0 && (
                                <p className="flex items-start gap-1.5 text-xs text-amber-700">
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    Mit einem einzigen Auftraggeber entsteht keine Teilrechnung.
                                    Für eine Aufteilung braucht es mindestens zwei.
                                </p>
                            )}

                            {saveError && (
                                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>{saveError}</span>
                                </div>
                            )}
                            {savedNote && (
                                <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                                    <Check className="h-3.5 w-3.5" /> {savedNote}
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
                    <div>
                        {hadStored && (
                            <Button
                                variant="ghost"
                                size="sm"
                                icon={<Trash2 className="h-3.5 w-3.5" />}
                                onClick={dropSplit}
                                disabled={gesperrt}
                                title={sperrHinweis}
                            >
                                Aufteilung aufheben
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            Schließen
                        </Button>
                        <Button onClick={save} disabled={!canSave} loading={saving} title={sperrHinweis}>
                            {saving ? 'Wird gespeichert …' : 'Speichern'}
                        </Button>
                    </div>
                </div>
            </div>

            {pickerOpen && (
                <CustomerPickerDialog
                    title="Auftraggeber in den Schlüssel aufnehmen"
                    intro="Jeder aufgenommene Auftraggeber bekommt beim Ausstellen eine eigene Rechnung über seinen Anteil."
                    customers={customers}
                    onPick={addParticipant}
                    onClose={() => setPickerOpen(false)}
                />
            )}
        </div>
    );
};
